import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { buildFinancialApiUrl } from '../utils/financial-api-url.util';
import { ErrorService } from '../shared/services/error.service';
import {
  AccountsReceivable,
  AccountsReceivableCreate,
  AccountsReceivableUpdate,
  AccountsReceivableFilter,
  AccountsReceivableSummary,
  AccountsReceivablePayment,
  AccountsReceivablePaymentBatchItem,
  AccountsReceivablePaymentBatchResponse,
  CancelPaymentsBatchResponse,
  AccountsReceivableBatchCreateResponse
} from '../models/AccountsReceivable';

export interface AccountsReceivablePagedResult {
  items: AccountsReceivable[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface PaginationOptions {
  page?: number;
  pageSize?: number;
}

export interface AccountsReceivableFilters extends PaginationOptions {
  customerId?: number;
  status?: string;
  costCenterId?: number;
  category?: string;
  dueDateStart?: string;
  dueDateEnd?: string;
  receiptDateStart?: string;
  receiptDateEnd?: string;
  searchTerm?: string;
  search?: string; // Alias para searchTerm conforme documentação
}

@Injectable({
  providedIn: 'root'
})
export class AccountsReceivableService {

  private financialUrl(path = ''): string {
    return buildFinancialApiUrl('accountsreceivable', path);
  }

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken');
    return new HttpHeaders({
      Authorization: token ? `Bearer ${token}` : ''
    });
  }

  // ===== CRUD OPERATIONS =====

  createAccountsReceivable(accountsReceivable: AccountsReceivableCreate): Observable<AccountsReceivable> {
    return this.http.post<AccountsReceivable>(this.financialUrl(), accountsReceivable, { headers: this.getAuthHeaders() }).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao criar conta a receber');
        return throwError(() => error);
      })
    );
  }

  /**
   * Cria múltiplas contas a receber em lote.
   * Endpoint: POST {financial}/accountsreceivable/batch
   */
  /**
   * Cria múltiplos títulos em lote.
   * POST /api/accountsreceivable/batch — corpo: array de AccountsReceivableCreate.
   */
  createAccountsReceivableBatch(batch: AccountsReceivableCreate[]): Observable<AccountsReceivableBatchCreateResponse> {
    return this.http
      .post<AccountsReceivableBatchCreateResponse>(this.financialUrl('/batch'), batch, { headers: this.getAuthHeaders() })
      .pipe(
        map((res: AccountsReceivableBatchCreateResponse | AccountsReceivable[]) => {
          if (Array.isArray(res)) {
            return { count: res.length, items: res };
          }
          return {
            count: res?.count ?? batch.length,
            items: res?.items ?? res?.created
          };
        }),
        catchError(error => {
          const msg = error?.error?.message || 'Erro ao criar contas a receber em lote';
          this.errorService.showErrorMessage(msg);
          return throwError(() => error);
        })
      );
  }

  getAccountsReceivableById(id: number): Observable<AccountsReceivable> {
    return this.http.get<AccountsReceivable>(this.financialUrl(`/${id}`)).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar conta a receber');
        return throwError(() => error);
      })
    );
  }

  /**
   * Histórico de pagamentos do título.
   * GET /api/accountsreceivable/{id}/payments
   */
  getPaymentsByAccountId(id: number): Observable<AccountsReceivablePayment[]> {
    return this.http.get<AccountsReceivablePayment[]>(this.financialUrl(`/${id}/payments`)).pipe(
      catchError(error => {
        if (error?.status === 404) {
          this.errorService.showErrorMessage('Conta a receber não encontrada');
        } else {
          this.errorService.showErrorMessage('Erro ao buscar histórico de pagamentos');
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Registrar um único pagamento.
   * POST /api/accountsreceivable/payments
   */
  registerPayment(payment: {
    accountsReceivableId: number;
    amount: number;
    paymentMethod: string;
    paymentDate: string;
    costCenterId?: number;
    reference?: string;
  }): Observable<AccountsReceivablePayment> {
    const body = {
      accountsReceivableId: payment.accountsReceivableId,
      amount: payment.amount,
      paymentMethod: payment.paymentMethod.trim(),
      paymentDate: payment.paymentDate,
      ...(payment.costCenterId != null && { costCenterId: payment.costCenterId }),
      ...(payment.reference != null && payment.reference.trim() !== '' && { reference: payment.reference.trim() })
    };
    return this.http.post<AccountsReceivablePayment>(this.financialUrl('/payments'), body).pipe(
      catchError(error => {
        const msg = error?.error?.message || 'Erro ao registrar pagamento.';
        this.errorService.showErrorMessage(msg);
        return throwError(() => error);
      })
    );
  }

  /**
   * Cancela pagamentos em lote (soft delete) e recalcula os títulos afetados.
   * POST /api/accountsreceivable/payments/cancel-batch
   */
  cancelPaymentsBatch(paymentIds: number[], cancelledBy?: string): Observable<CancelPaymentsBatchResponse> {
    const body: { paymentIds: number[]; cancelledBy?: string } = { paymentIds };
    if (cancelledBy?.trim()) {
      body.cancelledBy = cancelledBy.trim();
    }
    return this.http.post<CancelPaymentsBatchResponse>(this.financialUrl('/payments/cancel-batch'), body).pipe(
      map(res => ({
        cancelledPayments: res?.cancelledPayments ?? 0,
        affectedReceivables:
          res?.affectedReceivables ?? res?.affectedReceivableTitles ?? 0,
        affectedReceivableTitles: res?.affectedReceivableTitles ?? res?.affectedReceivables
      })),
      catchError(error => {
        const msg = error?.error?.message || 'Erro ao cancelar pagamentos em lote.';
        this.errorService.showErrorMessage(msg);
        return throwError(() => error);
      })
    );
  }

  /**
   * Registrar vários pagamentos de uma vez (lote). Uso interno; não exposto no front no momento.
   * POST /api/accountsreceivable/payments/batch
   */
  registerPaymentsBatch(payments: AccountsReceivablePaymentBatchItem[]): Observable<AccountsReceivablePaymentBatchResponse> {
    return this.http.post<any>(this.financialUrl('/payments/batch'), { payments }).pipe(
      map(res => ({
        count: res?.count ?? res?.Count ?? 0,
        payments: res?.payments ?? res?.Payments ?? []
      })),
      catchError(error => {
        const msg = error?.error?.message || error?.error?.errors?.join?.(' ') || 'Erro ao registrar pagamentos em lote';
        this.errorService.showErrorMessage(msg);
        return throwError(() => error);
      })
    );
  }

  getAccountsReceivableByDocumentNumber(documentNumber: string): Observable<AccountsReceivable> {
    return this.http.get<AccountsReceivable>(this.financialUrl(`/document/${documentNumber}`)).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar conta por documento');
        return throwError(() => error);
      })
    );
  }

  getAllAccountsReceivable(filters?: AccountsReceivableFilters): Observable<AccountsReceivablePagedResult> {
    let params = new HttpParams();
    
    params = params
      .set('page', String(filters?.page ?? 1))
      .set('pageSize', String(filters?.pageSize ?? 10));
    
    // Parâmetros de filtro
    if (filters?.customerId) params = params.set('customerId', filters.customerId.toString());
    if (filters?.status) params = params.set('status', filters.status);
    if (filters?.costCenterId) params = params.set('costCenterId', filters.costCenterId.toString());
    if (filters?.category) params = params.set('category', filters.category);
    if (filters?.dueDateStart) params = params.set('dueDateStart', filters.dueDateStart);
    if (filters?.dueDateEnd) params = params.set('dueDateEnd', filters.dueDateEnd);
    if (filters?.receiptDateStart) params = params.set('receiptDateStart', filters.receiptDateStart);
    if (filters?.receiptDateEnd) params = params.set('receiptDateEnd', filters.receiptDateEnd);
    
    // Busca por termo (aceita searchTerm ou search conforme documentação)
    if (filters?.searchTerm) {
      params = params.set('searchTerm', filters.searchTerm);
    } else if (filters?.search) {
      params = params.set('searchTerm', filters.search);
    }
    
    return this.http.get<any>(this.financialUrl(), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar contas a receber');
        return throwError(() => error);
      })
    );
  }

  updateAccountsReceivable(accountsReceivable: AccountsReceivableUpdate): Observable<AccountsReceivable> {
    return this.http.put<AccountsReceivable>(this.financialUrl(`/${accountsReceivable.id}`), accountsReceivable).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao atualizar conta a receber');
        return throwError(() => error);
      })
    );
  }

  deleteAccountsReceivable(id: number): Observable<void> {
    return this.http.delete<void>(this.financialUrl(`/${id}`)).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao deletar conta a receber');
        return throwError(() => error);
      })
    );
  }

  // ===== FILTER OPERATIONS =====

  getPendingReceipts(options?: PaginationOptions): Observable<AccountsReceivablePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl('/pending'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar recebimentos pendentes');
        return throwError(() => error);
      })
    );
  }

  getOverdueReceipts(options?: PaginationOptions): Observable<AccountsReceivablePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl('/overdue'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar recebimentos vencidos');
        return throwError(() => error);
      })
    );
  }

  getByDueDateRange(start: string, end: string, options?: PaginationOptions): Observable<AccountsReceivablePagedResult> {
    let params = new HttpParams().set('start', start).set('end', end);
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl('/due-range'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar por faixa de vencimento');
        return throwError(() => error);
      })
    );
  }

  getByReceiptDateRange(start: string, end: string, options?: PaginationOptions): Observable<AccountsReceivablePagedResult> {
    let params = new HttpParams().set('start', start).set('end', end);
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl('/receipt-range'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar por faixa de recebimento');
        return throwError(() => error);
      })
    );
  }

  getAccountsReceivableByCustomer(customerId: number, options?: PaginationOptions): Observable<AccountsReceivablePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl(`/customer/${customerId}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar contas por cliente');
        return throwError(() => error);
      })
    );
  }

  getByCostCenter(costCenterId: number, options?: PaginationOptions): Observable<AccountsReceivablePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl(`/costcenter/${costCenterId}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar por centro de custo');
        return throwError(() => error);
      })
    );
  }

  getAccountsReceivableByStatus(status: string, options?: PaginationOptions): Observable<AccountsReceivablePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl(`/status/${status}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar contas por status');
        return throwError(() => error);
      })
    );
  }

  getAccountsReceivableByCategory(category: string, options?: PaginationOptions): Observable<AccountsReceivablePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl(`/category/${category}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar contas por categoria');
        return throwError(() => error);
      })
    );
  }

  // ===== SUMMARY OPERATIONS =====

  getTotalPendingAmount(): Observable<{ totalPendingAmount: number }> {
    return this.http.get<{ totalPendingAmount: number }>(this.financialUrl('/total-pending')).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar total pendente');
        return throwError(() => error);
      })
    );
  }

  getTotalOverdueAmount(): Observable<{ totalOverdueAmount: number }> {
    return this.http.get<{ totalOverdueAmount: number }>(this.financialUrl('/total-overdue')).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar total em atraso');
        return throwError(() => error);
      })
    );
  }

  // ===== VALIDATION =====

  checkDocumentNumberExists(documentNumber: string, excludeId?: number): Observable<{ exists: boolean }> {
    let params = new HttpParams();
    if (excludeId) {
      params = params.set('excludeId', excludeId.toString());
    }
    
  return this.http.get<{ exists: boolean }>(this.financialUrl(`/check-document/${documentNumber}`), { params }).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao verificar documento');
        return throwError(() => error);
      })
    );
  }

  // ===== SEARCH OPERATIONS =====

  searchAccountsReceivable(term: string): Observable<AccountsReceivable[]> {
    const params = new HttpParams().set('search', term);
  return this.http.get<AccountsReceivable[]>(this.financialUrl('/search'), { params }).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar contas a receber');
        return throwError(() => error);
      })
    );
  }

  getBySaleOrderNumber(saleOrderNumber: string, options?: PaginationOptions): Observable<AccountsReceivablePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl(`/sale-order/${encodeURIComponent(saleOrderNumber)}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar por pedido de venda');
        return throwError(() => error);
      })
    );
  }

  getByInvoiceNumber(invoiceNumber: string, options?: PaginationOptions): Observable<AccountsReceivablePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
  return this.http.get<any>(this.financialUrl(`/invoice/${encodeURIComponent(invoiceNumber)}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar por nota fiscal');
        return throwError(() => error);
      })
    );
  }

  // ===== FILTER WITH PARAMS =====

  getAccountsReceivableWithFilters(filters: AccountsReceivableFilter): Observable<AccountsReceivable[]> {
    let params = new HttpParams();
    
    if (filters.customerId) params = params.set('customerId', filters.customerId.toString());
    if (filters.status) params = params.set('status', filters.status);
    if (filters.category) params = params.set('category', filters.category);
    if (filters.dueDateFrom) params = params.set('dueDateFrom', filters.dueDateFrom.toString());
    if (filters.dueDateTo) params = params.set('dueDateTo', filters.dueDateTo.toString());
    if (filters.amountFrom) params = params.set('amountFrom', filters.amountFrom.toString());
    if (filters.amountTo) params = params.set('amountTo', filters.amountTo.toString());

  return this.http.get<AccountsReceivable[]>(this.financialUrl('/filter'), { params }).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar contas com filtros');
        return throwError(() => error);
      })
    );
  }

  private mapPagedResponse(response: any): AccountsReceivablePagedResult {
    const rawItems = Array.isArray(response?.Items)
      ? response.Items
      : Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

    const items = rawItems.map((item: any) => item as AccountsReceivable);

    const page = response?.Page ?? response?.page ?? 1;
    const pageSize = response?.PageSize ?? response?.pageSize ?? items.length;
    const totalItems = response?.TotalItems ?? response?.totalItems ?? response?.TotalRecords ?? response?.totalRecords ?? items.length;
    const totalPages = response?.TotalPages ?? response?.totalPages ?? (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1);

    return {
      items,
      page,
      pageSize,
      totalItems,
      totalPages,
    };
  }
}
